// https://vuex.vuejs.org/en/actions.html

import API from '../api';
import { OAUTH_OPTIONS, UserRole } from '../utils/constants';
import ClientOAuth2 from 'client-oauth2';
import uploadService from '../api/upload.service';
import { fileNameFrom, saveFile } from '../utils';

function setStorage(token) {

  const { accessToken, refreshToken, tokenType, expires } = token;

  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
  localStorage.setItem('tokenType', tokenType);
  localStorage.setItem('tokenExpiration', expires.getTime());
}

// The login in flight, if any. Two password logins of the same user within one second get the
// same token from the server, which stored it twice and then failed every request of that user
// until it expired. A second request while one is running gets the same answer instead.
let pendingLogin = null;

function variable(state, name, service, id) {
  const v = state.vars[name];
  return v && v.service === service && v.id === id ? v : null;
}

export default {

  login({ dispatch }, user) {
    if ( pendingLogin ) return pendingLogin;

    pendingLogin = new Promise((resolve, reject) => {

      new ClientOAuth2(OAUTH_OPTIONS).owner.getToken(user.name, user.password)
        .then(response => {

          this.state.token = response.accessToken;
          setStorage(response);

          dispatch('currentUser')
            .then(resolve)
            .catch(reject);

        }).catch(err => {

          reject(err);
        })
    }).finally(() => { pendingLogin = null; });

    return pendingLogin;
  },

  refreshToken({ dispatch }) {
    return new Promise((resolve, reject) => {

      const accessToken = localStorage.getItem('accessToken');

      if (accessToken && accessToken != 'undefined') {

        const tokenExpiration = localStorage.getItem('tokenExpiration');
        const now = Date.now();

        if (parseInt(tokenExpiration) > now) {

          dispatch('currentUser')
            .then(resolve)
            .catch(err => {

              console.warn(err);
              localStorage.removeItem('accessToken');
              this.state.userID = -1;
              resolve(null);

            });

          return;
        }

        const oauth = new ClientOAuth2(OAUTH_OPTIONS);
        const refreshToken = localStorage.getItem('refreshToken');
        const tokenType = localStorage.getItem('tokenType');

        var token = oauth.createToken(accessToken, refreshToken, tokenType, { data: {} });
        token.expiresIn(new Date(tokenExpiration));

        token.refresh().then(response => {

          this.state.token = response.accessToken;
          setStorage(response);

          dispatch('currentUser')
            .then(resolve)
            .catch(reject);

        }).catch(err => {

          this.state.userID = -1;
          reject(err);
        });

      } else {

        this.state.userID = -1;
        resolve(null);

      }
    });
  },

  logout({ dispatch }) {
    this.state.userID = -1;
    this.state.user = null;
    this.state.isAdmin = false;
    this.state.isPayrollAdmin = false;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    dispatch('socket/off');
  },

  // API Services

  api( context, payload ) {
    return new Promise((resolve, reject) => {

      const accessToken = localStorage.getItem('accessToken');
      const { target, params } = payload;
      const [ service, method ] = target.split('/');
      const APIService = API[service];

      if ( ! accessToken ) return reject();

      APIService[method].apply( APIService, [accessToken].concat( params || [] ))
        .then( response => {
          if ( response.data.error ) {

            if ( response.error == "invalid_token" ) {
              //Seteamos la expiración del token a 0
              localStorage.setItem('tokenExpiration', 0);
              reject( response );
              return;
            }
            else if ( response.data.error < 0 ) {
              reject( response );
              return;
            }
          }
          resolve( response.data.object );
        })
        .catch( err => reject([ err ]));
    });
  },

  page({ dispatch }, payload ) {
    return new Promise(( resolve, reject ) => {

      const { service, subservice, method, page, filters, order, ascending } = payload;
      const params = payload.params || { page: page || 1, order, ascending, filters };
      const target = `${ subservice || service }/${ method || 'all' }`;

      dispatch( 'api', { target, params }).then( response => {
        resolve({ ...params, ...response });
      }).catch( reject );
    });
  },

  single({ dispatch }, payload ) {
    return new Promise(( resolve, reject ) => {

      const id = payload.id || 'new';
      const { service, subservice, method } = payload;
      const params = payload.params || { id };

      if ( id === 'new' ) {

        resolve({});

      } else {

        const target = `${ subservice || service }/${ method || 'get' }`;

        dispatch( 'api', { target, params })
          .then( resolve )
          .catch( reject );
      }
    });
  },

  exportData({ dispatch }, payload ) {
    return new Promise(( resolve, reject ) => {

      const { service, filters, method, id } = payload;

      const target = `${service}/${method || 'export'}`;

      dispatch( 'api', { target: target, params: { id, filters }})
        .then( resolve )
        .catch( reject );

    });
  },

  // Calls an endpoint that produces a file, the way the api action calls any other, but asks
  // for the file itself (direct=true) and saves it, rather than for the address of a copy left in
  // the server's public folder. Resolves with the name it was saved as. A refusal or a failure
  // still comes as JSON and is rejected the same way as in the api action, so the callers can
  // hand it to console/error as they do now.
  download( context, payload ) {
    return new Promise(( resolve, reject ) => {

      const accessToken = localStorage.getItem( 'accessToken' );
      const { target, params, fileName } = payload;
      const [ service, method ] = target.split( '/' );

      if ( ! accessToken ) return reject();

      API[ service ][ method ]( accessToken, params, { params: { direct: true }, responseType: 'blob' })
        .then( response => {
          const blob = response.data;
          if (( blob.type || '' ).includes( 'json' )) {
            return blob.text().then( text => {
              const data = JSON.parse( text );
              // A server that predates direct=true still answers with the address of the file.
              if ( data.error === 0 && typeof data.object === 'string' ) {
                window.open( data.object, '_blank' );
                return resolve( data.object );
              }
              reject({ data });
            });
          }
          const name = fileNameFrom( response.headers[ 'content-disposition' ], fileName || 'export' );
          saveFile( blob, name );
          resolve( name );
        })
        .catch( err => reject([ err ]));
    });
  },

  // The export of a listing, saved as a file. Takes the same payload as exportData, which is
  // still there for the calls that use it for something else, such as closing the payroll.
  downloadData({ dispatch }, payload ) {
    const { service, filters, method, id } = payload;
    return dispatch( 'download', { target: `${ service }/${ method || 'export' }`, params: { id, filters }});
  },

  importData({ dispatch }, payload) {
    return new Promise((resolve, reject) => {

      const { service, data, method } = payload;

      const target = `${service}/${method || 'import'}`;

      dispatch('api', { target: target, params: data })
        .then(resolve)
        .catch(reject);
    });
  },

  cancelRequest(context, opt) {
    const { target, message } = opt;
    return API[target].cancel(message);
  },

  currentUser({ dispatch, commit }) {
    return new Promise((resolve, reject) => {
      dispatch('api', { target: 'Users/current' })
        .then(response => {

          this.state.user = response;
          this.state.userID = response.id;
          // PayrollAdmin sits above Master: it can do everything a Master can, plus the four
          // economic screens. Keeping both under isAdmin means every place that already asks
          // for it covers the new role, the same way the server does with isSuperAdmin().
          this.state.isAdmin = response.id > 0
            && ( response.role === UserRole.SUPERADMIN || response.role === UserRole.PAYROLL_ADMIN );
          this.state.isPayrollAdmin = response.id > 0 && response.role === UserRole.PAYROLL_ADMIN;
          this.state.isKAM = response.id > 0 && response.role === UserRole.KAM;
          this.state.isManager = response.id > 0 && response.role === UserRole.MANAGER;
          this.state.isClient = response.id > 0 && response.role === UserRole.CLIENT;
          this.state.isRRHH = response.id > 0 && response.role === UserRole.RRHH;

          commit('socket/set', {
            chats: response.chats,
            issues: response.issues,
            tasks: response.tasks,
            evaluations: response.evaluations
          });

          // Connect to Web Sockets
          if ( process.env.NODE_ENV == 'production' ) {
            dispatch('socket/on')
              .then(() => dispatch('socket/subscribe', {
                [response.id]: res => commit('socket/set', res.data),
              }))
              .catch(console.warn);
          }

          resolve(response);

        }).catch(err => {
          //console.log( [err] );
          this.state.user = null;
          this.state.userId = -1;
          this.state.isAdmin = false;
          this.state.isPayrollAdmin = false;
          reject(err);
        })
    });
  },

  connectSocket({ dispatch, state, commit }) {
    console.log("Connecting socket for user " + state.userID);
    if (state.user && state.userID) {
      dispatch('socket/on')
        .then(() => dispatch('socket/subscribe', {
          [state.userID]: res => commit('socket/set', res.data),
        })).catch(err => {
          console.warn("STOMP: " + err);
          console.log('STOMP: Reconecting in 10 seconds');
          setTimeout(() => {
            dispatch('connectSocket')
          }, 10000);
        });
    }
  },

  vars({ dispatch, commit, state }, params ) {
    return new Promise( resolve => {

      var count = 0;
      const { core } = state;
      const { service, subservice } = core;
      const vars = {
        item: variable( state, 'item', service, params.id ),
        subitem: variable( state, 'subitem', subservice, params.index )
      };

      // Reset vars
      commit( 'setVars', vars );

      // Get value vars
      if ( !vars.item && service && params.id && params.id !== 'new' ) {

        dispatch( 'api', { target: `${ service }/get`, params: { id: params.id } })
          .then( data => {
            vars.item = { service, data, id: params.id };
            commit('setVars', vars);
          })
          .catch(() => commit('setVars', vars))
          .finally(() => {
            count++;
            if (count === 2) resolve(vars);
          });

      } else {
        count++;
      }

      if (!vars.subitem && subservice && params.index && params.index !== 'new') {

        dispatch('api', { target: `${subservice}/get`, params: { id: params.index } })
          .then(data => {
            vars.subitem = { service: subservice, data, id: params.index };
            commit('setVars', vars);
          })
          .catch(() => commit('setVars', vars))
          .finally(() => {
            count++;
            if (count === 2) resolve(vars);
          });

      } else {
        count++;
        if (count === 2) resolve(vars);
      }
    });
  },

  uploadImage(context, formData) {
    return new Promise((resolve, reject) => {

      const accessToken = localStorage.getItem('accessToken')

      if (accessToken != null) {
        uploadService.uploadPicture(accessToken, formData)
          .then(response => {
            // handle success
            //console.log( response );

            if (response.data.error < 0) {
              reject(response.data);
            } else {
              resolve(response.data.object)
            }
          })
          .catch(err => {
            console.log(err);
            reject(err);
          });
      }
    });
  },

  uploadFile(context, formData) {
    return new Promise((resolve, reject) => {

      const accessToken = localStorage.getItem('accessToken')

      if (accessToken != null) {
        uploadService.uploadFile(accessToken, formData)
          .then(response => {
            // handle success
            //console.log( response );

            if (response.data.error < 0) {
              reject(response.data);
            } else {
              resolve(response.data.object)
            }
          })
          .catch(err => {
            console.log(err);
            reject(err);
          });
      }
    });
  },
}
